import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatPriceFR, formatSurfaceFR } from '../../utils/formatFR';

/**
 * PropertyCardShared - Carte de bien pour les collections partagées (design identique à la liste interne)
 */
const PropertyCardShared = ({ property, token }) => {
  const navigate = useNavigate();

  // Extraire les données
  const photos = property.medias?.photos || property.photos || [];
  const firstPhoto = photos.length > 0 ? (photos[0].url || photos[0]) : null;
  
  const titre = property.bien_general?.titre_annonce || 
               property.enseigne?.enseigne || 
               property.bien_general?.type_bien ||
               'Bien immobilier';
               
  const ville = property.bien_general?.ville_generale ||
               property.informations_confidentielles?.ville || 
               '';
               
  const prix = property.honoraires_calcul?.prix_vente || 
              property.bien_general?.prix_vente || 
              property.bien_general?.prix_estime ||
              0;
              
  const typeBien = property.bien_general?.type_bien || '';
  const activitePrincipale = property.bien_general?.activite_principale || '';
  
  const surface = property.composition?.superficie_totale ||
                 property.composition?.surface_totale ||
                 property.bien_general?.surface_totale ||
                 property.enseigne?.superficie ||
                 0;
  
  const numeroMandat = property.numero_mandat || '';
  
  const disponibilite = property.bien_general?.statut_bien || 
                       property.bien_general?.disponibilite || 
                       property.disponibilite || 
                       'Libre';
                       
  const dateCreation = property.created_at ? new Date(property.created_at).toLocaleDateString('fr-FR') : '';
  
  const handleClick = () => {
    navigate(`/shared/${token}/${property.id}`);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden">
      {/* Image avec badges */}
      <div className="relative h-56 bg-gradient-to-br from-gray-200 to-gray-300 cursor-pointer" onClick={handleClick}>
        {firstPhoto ? (
          <img 
            src={firstPhoto} 
            alt={titre}
            className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <i className="bi bi-image text-5xl"></i>
          </div>
        )}
        
        {/* Badge Vente en haut à droite */}
        <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1.5 rounded-lg text-sm font-bold shadow-lg flex items-center gap-1">
          <i className="bi bi-arrow-up-short"></i>
          Vente
        </div>
        
        {/* Badge Prix en bas à gauche */}
        {prix > 0 && (
          <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg">
            <div className="text-2xl font-bold text-gray-900 num">
              {formatPriceFR(prix)}
            </div>
          </div>
        )}
        
        {/* Badge nombre de photos en bas à droite */}
        {photos.length > 1 && (
          <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1">
            <i className="bi bi-image"></i>
            {photos.length}
          </div>
        )}
      </div>

      {/* Informations */}
      <div className="p-5">
        {/* Titre */}
        <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 cursor-pointer hover:text-blue-600 transition-colors min-h-[3.5rem]" onClick={handleClick}>
          {titre}
        </h3>
        
        {/* Informations principales */}
        <div className="space-y-2.5 mb-4">
          {/* Prix */}
          {prix > 0 && (
            <div className="flex items-center text-emerald-600 font-bold text-xl num">
              <i className="bi bi-currency-euro mr-2"></i>
              {formatPriceFR(prix)}
            </div>
          )}
          
          {/* Ville */}
          {ville && (
            <div className="flex items-center text-gray-700 text-sm">
              <i className="bi bi-geo-alt-fill mr-2 text-slate-500"></i>
              <span className="font-medium">{ville}</span>
            </div>
          )}
          
          {/* Référence mandat */}
          {numeroMandat && (
            <div className="flex items-center text-gray-600 text-sm">
              <i className="bi bi-tag-fill mr-2 text-blue-500"></i>
              <span>{numeroMandat}</span>
            </div>
          )}
          
          {/* Type de bien */}
          {typeBien && (
            <div className="flex items-center text-gray-600 text-sm">
              <i className="bi bi-shop mr-2 text-indigo-500"></i>
              <span>{typeBien}</span>
            </div>
          )}
          
          {/* Activité principale */}
          {activitePrincipale && (
            <div className="flex items-center text-gray-600 text-sm">
              <i className="bi bi-briefcase mr-2 text-orange-500"></i>
              <span className="line-clamp-1">{activitePrincipale}</span>
            </div>
          )}
          
          {/* Surface */}
          {surface > 0 && (
            <div className="flex items-center text-gray-600 text-sm num">
              <i className="bi bi-rulers mr-2 text-teal-500"></i>
              <span>{formatSurfaceFR(surface)}</span>
            </div>
          )}
        </div>
        
        {/* Footer avec disponibilité */}
        <div className="pt-3 border-t border-gray-100">
          <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold">
            {disponibilite}
          </span>
        </div>
        
        {/* Date */}
        {dateCreation && (
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-4">
            <i className="bi bi-calendar3"></i>
            <span>{dateCreation}</span>
          </div>
        )}
        
        {/* Boutons d'action */}
        <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
          <button 
            onClick={handleClick}
            className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 px-4 py-2.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
          >
            <i className="bi bi-eye"></i>
            Voir
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropertyCardShared;
